import { ChevronDown, MoreHorizontal, Plus, Search } from "lucide-react";
import { useEffect, useRef, useState, type ChangeEvent, type JSX } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { DensityToggle } from "@/shared/components/DensityToggle";
import { FilterChips, type Chip } from "@/shared/components/FilterChips";
import type { Density } from "@/shared/hooks/useDensity";
import { cn } from "@/shared/lib/cn";
import { toast } from "@/shared/lib/toast";

import type {
  FilterUpdate,
  InventoryFilterState,
  Marketplace,
  StockStatus,
} from "../lib/url-state";

export type InventoryTab = "all" | "single" | "bundles";

interface TabCounts {
  all?: number;
  single?: number;
  bundles?: number;
}

interface InventoryToolbarProps {
  filter: InventoryFilterState;
  onFilterChange: (updates: FilterUpdate) => void;
  /** Active tab — lifted to the Inventory page so it can switch stock/bundles view. */
  activeTab: InventoryTab;
  onTabChange: (tab: InventoryTab) => void;
  counts?: TabCounts;
  onCreateSku?: () => void;
  density?: Density;
  onDensityChange?: (d: Density) => void;
  onExportCsv?: () => void;
  /** Opens the bulk "Sync Multi" modal (PRD v1.8 §4.6.3.2-C). */
  onSyncMulti?: () => void;
  /** Shows the "Import transfers" More-menu entry (stock:write). */
  canImport?: boolean;
  /** Hides the All/Single/Bundles tab row — the Brand: Unmapped filter replaces
   *  the tabbed stock/bundles view with its own single list, so the tabs (and
   *  their now-irrelevant counts) would be misleading while it's active. */
  hideTabs?: boolean;
}

const MARKETPLACES: { value: Marketplace; label: string }[] = [
  { value: "shopee", label: "Shopee" },
  { value: "lazada", label: "Lazada" },
  { value: "tiktok", label: "TikTok" },
];

const STATUSES: { value: StockStatus; labelKey: string }[] = [
  { value: "healthy", labelKey: "status.healthy" },
  { value: "low_stock", labelKey: "status.low_stock" },
  { value: "out_of_stock", labelKey: "status.out_of_stock" },
  { value: "oversold", labelKey: "status.oversold" },
];

const TABS: {
  tab: InventoryTab;
  testid: string;
  labelKey: string;
  countKey: keyof TabCounts;
}[] = [
  { tab: "all", testid: "tab-all", labelKey: "tab.all", countKey: "all" },
  { tab: "single", testid: "tab-single", labelKey: "tab.single", countKey: "single" },
  { tab: "bundles", testid: "tab-bundles", labelKey: "tab.bundles", countKey: "bundles" },
];

const SELECT_CLASS =
  "h-[var(--control-h)] appearance-none rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-[14px] pr-9 text-[13.5px] font-medium text-[var(--text-primary)] shadow-[var(--sh-xs)] outline-none transition hover:border-[#c7cdd9] focus:border-[var(--brand-500)] focus-ring";

export function InventoryToolbar({
  filter,
  onFilterChange,
  activeTab,
  onTabChange,
  counts,
  onCreateSku,
  density,
  onDensityChange,
  onExportCsv,
  onSyncMulti,
  canImport = true,
  hideTabs = false,
}: InventoryToolbarProps): JSX.Element {
  const { t } = useTranslation("inventory");
  const isBundles = activeTab === "bundles";
  const [moreOpen, setMoreOpen] = useState(false);
  const [morePos, setMorePos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  function openMore(): void {
    const r = moreBtnRef.current?.getBoundingClientRect();
    if (r) setMorePos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setMoreOpen(true);
  }

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (
        moreMenuRef.current?.contains(e.target as Node) ||
        moreBtnRef.current?.contains(e.target as Node)
      )
        return;
      setMoreOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [moreOpen]);

  const chips: Chip[] = [
    filter.brand === "unmapped"
      ? {
          key: "brand",
          label: t("filter.brand_unmapped"),
          onDismiss: () => onFilterChange({ brand: undefined, offset: 0 }),
        }
      : null,
    filter.marketplace
      ? {
          key: "mkp",
          label:
            MARKETPLACES.find((m) => m.value === filter.marketplace)?.label ?? filter.marketplace,
          onDismiss: () => onFilterChange({ marketplace: undefined, offset: 0 }),
        }
      : null,
    filter.status
      ? {
          key: "status",
          label: t(`status.${filter.status}`),
          onDismiss: () => onFilterChange({ status: undefined, offset: 0 }),
        }
      : null,
    filter.search
      ? {
          key: "search",
          label: `"${filter.search}"`,
          onDismiss: () => onFilterChange({ search: "", offset: 0 }),
        }
      : null,
  ].filter(Boolean) as Chip[];

  function handleClearAll() {
    onFilterChange({
      marketplace: undefined,
      status: undefined,
      search: "",
      brand: undefined,
      offset: 0,
    });
  }

  function handleSearch(e: ChangeEvent<HTMLInputElement>) {
    onFilterChange({ search: e.target.value, offset: 0 });
  }

  function handleMarketplace(e: ChangeEvent<HTMLSelectElement>) {
    onFilterChange({
      marketplace: (e.target.value as Marketplace) || undefined,
      offset: 0,
    });
  }

  function handleStatus(e: ChangeEvent<HTMLSelectElement>) {
    onFilterChange({
      status: (e.target.value as StockStatus) || undefined,
      offset: 0,
    });
  }

  // Marketplace applies to both stock and bundles: a bundle row is already
  // exploded one-per-marketplace (own_stock), so filtering to one marketplace
  // is a client-side row filter in BundlesPanel — no /v1/listings query param
  // needed. Status has no bundle-row equivalent yet (ListingOwnStock carries
  // no status_code), so it stays stock-only.
  const marketplaceSelect = (
    <div className="relative">
      <select
        value={filter.marketplace ?? ""}
        onChange={handleMarketplace}
        className={SELECT_CLASS}
        data-testid="filter-marketplace"
        aria-label={t("filter.all_marketplaces")}
      >
        <option value="">{t("filter.all_marketplaces")}</option>
        {MARKETPLACES.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-[11px] top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]"
        aria-hidden
      />
    </div>
  );

  // Search applies to both stock and bundles (BE supports `search` on both
  // /v1/stock and /v1/listings) — rendered in both filter-row branches below,
  // unlike status which the bundles endpoint does not support yet.
  const searchInput = (
    <div className="relative min-w-[200px] flex-1">
      <Search
        className="pointer-events-none absolute left-[13px] top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-[var(--text-subtle)]"
        aria-hidden
      />
      <input
        type="search"
        value={filter.search}
        onChange={handleSearch}
        placeholder={t("filter.search_placeholder")}
        className="h-[var(--control-h)] w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white pl-[38px] pr-3.5 text-[13.5px] font-medium outline-none transition focus:border-[var(--brand-500)] focus-ring"
        style={{ boxShadow: "var(--sh-xs)" }}
        data-testid="filter-search"
      />
    </div>
  );

  // The unified Create button. Shown in ALL tabs (stock + bundles); the page
  // routes it to the Create modal with the right `defaultBundle`. Rendered inline
  // with the stock filters and as a standalone right-aligned row in the Bundles
  // view (which has no filter row).
  const createButton = onCreateSku ? (
    <button
      type="button"
      onClick={onCreateSku}
      data-testid="toolbar-create-sku"
      className="ml-auto inline-flex h-[var(--control-h)] items-center gap-[7px] rounded-[var(--control-r)] px-4 text-[13.5px] font-bold text-white transition hover:-translate-y-px"
      style={{
        background: "var(--grad-primary-btn)",
        boxShadow:
          "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5),inset 0 1px 0 rgba(255,255,255,.22)",
      }}
    >
      <Plus className="h-[15px] w-[15px]" aria-hidden />
      {t("toolbar.create")}
    </button>
  ) : null;

  return (
    <div className="mb-4">
      {/* Tab row — hidden while the Brand: Unmapped filter replaces the
          tabbed stock/bundles view with its own single SKU-master list. */}
      {!hideTabs && (
        <div
          className="mb-[18px] flex items-center gap-1.5 border-b border-[var(--premium-border)]"
          role="tablist"
        >
          {TABS.map(({ tab, testid, labelKey, countKey }) => {
            const active = activeTab === tab;
            const count = counts?.[countKey];
            return (
              <button
                key={testid}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onTabChange(tab)}
                data-testid={testid}
                className={[
                  "relative mr-[18px] px-1 py-2.5 text-sm font-semibold transition-colors",
                  active
                    ? "text-[var(--brand-700)]"
                    : "text-[var(--text-subtle)] hover:text-[var(--text-secondary)]",
                ].join(" ")}
              >
                {t(labelKey)}
                {count !== undefined && (
                  <span className="ml-[7px] rounded-full bg-[var(--surface-sunken)] px-[7px] py-px text-[11px] font-bold text-[var(--text-subtle)]">
                    {count}
                  </span>
                )}
                {active && (
                  <span className="absolute -bottom-px left-0 right-[18px] h-[2.5px] rounded-[3px] bg-[var(--brand-500)]" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Filter row — stock-specific, hidden in the Bundles view */}
      {!isBundles && (
        <div className="flex flex-wrap items-center gap-2.5">
          {marketplaceSelect}

          {/* Status select */}
          <div className="relative">
            <select
              value={filter.status ?? ""}
              onChange={handleStatus}
              className={SELECT_CLASS}
              data-testid="filter-status"
              aria-label={t("filter.all_statuses")}
            >
              <option value="">{t("filter.all_statuses")}</option>
              {STATUSES.map(({ value, labelKey }) => (
                <option key={value} value={value}>
                  {t(labelKey)}
                </option>
              ))}
            </select>
            <ChevronDown
              className="pointer-events-none absolute right-[11px] top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-subtle)]"
              aria-hidden
            />
          </div>

          <button
            type="button"
            aria-pressed={filter.brand === "unmapped"}
            data-testid="filter-brand-unmapped"
            onClick={() =>
              onFilterChange({ brand: filter.brand === "unmapped" ? undefined : "unmapped", offset: 0 })
            }
            className={cn(
              "inline-flex h-[var(--control-h)] items-center rounded-[var(--control-r)] border px-3.5 text-[13.5px] font-semibold shadow-[var(--sh-xs)] transition",
              filter.brand === "unmapped"
                ? "border-[var(--brand-500)] bg-[var(--brand-50)] text-[var(--brand-700)]"
                : "border-[var(--premium-border-strong)] bg-white text-[var(--text-primary)] hover:bg-[var(--canvas)]",
            )}
          >
            {t("filter.brand_unmapped")}
          </button>

          {searchInput}

          {onDensityChange && density !== undefined && (
            <DensityToggle value={density} onChange={onDensityChange} />
          )}

          {/* More menu */}
          <button
            ref={moreBtnRef}
            type="button"
            onClick={() => (moreOpen ? setMoreOpen(false) : openMore())}
            data-testid="toolbar-more"
            title={t("toolbar.more")}
            aria-label={t("toolbar.more")}
            className="grid h-[var(--control-h)] w-[var(--control-h)] place-items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white text-[var(--text-secondary)] shadow-[var(--sh-xs)] transition hover:border-[#c7cdd9] hover:bg-[var(--canvas)] focus-ring"
            aria-expanded={moreOpen}
            aria-haspopup="menu"
          >
            <MoreHorizontal className="h-[18px] w-[18px]" aria-hidden />
          </button>
          {moreOpen &&
            createPortal(
              <div
                ref={moreMenuRef}
                role="menu"
                style={{ position: "fixed", top: morePos.top, right: morePos.right, width: 192 }}
                className="z-[80] rounded-xl border border-[var(--premium-border)] bg-white p-1.5 shadow-[var(--sh-pop)]"
              >
                {[
                  { key: "export_csv", label: t("toolbar.export_csv") },
                  ...(canImport
                    ? [{ key: "import_transfers", label: t("toolbar.import_transfers") }]
                    : []),
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    role="menuitem"
                    data-testid={`toolbar-more-${key}`}
                    onClick={() => {
                      setMoreOpen(false);
                      if (key === "export_csv" && onExportCsv) {
                        onExportCsv();
                      } else {
                        toast.info(t("toolbar.coming_soon", { label }));
                      }
                    }}
                    className="flex w-full items-center rounded-lg px-2.5 py-2 text-sm text-[var(--text-primary)] transition hover:bg-[var(--canvas)]"
                  >
                    {label}
                  </button>
                ))}
              </div>,
              document.body,
            )}

          {/* Promoted out of the "More" menu (PRD v1.8 §4.6.3.2-C / A.7): bulk
              stock sync is common enough to earn a visible control. */}
          {onSyncMulti && (
            <button
              type="button"
              onClick={onSyncMulti}
              data-testid="toolbar-sync-multi"
              className="inline-flex h-[var(--control-h)] items-center gap-[7px] rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3.5 text-[13.5px] font-semibold text-[var(--text-primary)] shadow-[var(--sh-xs)] transition hover:border-[#c7cdd9] hover:bg-[var(--canvas)]"
            >
              {t("toolbar.sync_multi")}
            </button>
          )}

          {createButton}
        </div>
      )}

      {/* Bundles view: marketplace + search. Marketplace filters the already
          per-marketplace-exploded bundle rows client-side (BundlesPanel);
          status has no bundle-row equivalent yet, so it stays stock-only. */}
      {isBundles && (
        <div className="flex flex-wrap items-center gap-2.5">
          {marketplaceSelect}
          {searchInput}
          {createButton}
        </div>
      )}

      {chips.length > 0 && (
        <div className="mt-3">
          <FilterChips chips={chips} onClearAll={handleClearAll} />
        </div>
      )}
    </div>
  );
}
